/**
 * `materialWire.ts` — conversions between the web `MaterialDto` (Dynamo row
 * shape) and Unity's JsonUtility event shape for `update_material` (tc 23,
 * `MaterialEventData.materialData`).
 *
 * Dual-encoding traps (verified against `Models/Data/MaterialData.cs`):
 *  - Dynamo `Cutoff` ↔ wire `alphaCutoff`.
 *  - Dynamo `OcclusionStrength` ↔ wire `occulusionStrength` (the
 *    misspelling IS the wire contract).
 *  - Dynamo colors/tiling are `M{r,g,b,a}` / `M{x,y}` records; the wire
 *    uses `[{name,val}]` pair arrays. `textures`/`filterModes` are
 *    `[{name,val}]` STRING pairs on the wire vs string maps on Dynamo.
 *
 * The event has no `sceneId` — `MaterialEventData` extends
 * `TargetProjectEventData` only.
 */

import type { MaterialDto } from "./materialDto";
import { materialNumber, materialStringMap } from "./materialDto";
import { toNameFloatMap } from "./nameFloatMap";

type FloatPair = { readonly name: string; readonly val: number };
type StringPair = { readonly name: string; readonly val: string };

function recordToPairs(value: unknown, keys: ReadonlyArray<string>): FloatPair[] {
  const map = toNameFloatMap(value);
  return keys
    .filter((key) => typeof map[key] === "number")
    .map((key) => ({ name: key, val: map[key] as number }));
}

function stringMapToPairs(value: unknown): StringPair[] {
  return Object.entries(materialStringMap(value)).map(([name, val]) => ({ name, val }));
}

function pairsToRecord(value: unknown): Record<string, number> | undefined {
  if (value === undefined || value === null) return undefined;
  return { ...toNameFloatMap(value) };
}

function pairsToStringMap(value: unknown): Record<string, string> | undefined {
  if (value === undefined || value === null) return undefined;
  return materialStringMap(value);
}

const RGBA = ["r", "g", "b", "a"] as const;
const XY = ["x", "y"] as const;

/** Web `MaterialDto` → JsonUtility `materialData` payload. */
export function materialDtoToWire(dto: MaterialDto): Record<string, unknown> {
  return {
    name: dto.Name ?? "",
    id: dto.ID,
    userPreset: dto.UserPreset ?? false,
    workflowMode: materialNumber(dto.WorkflowMode, 0),
    surface: materialNumber(dto.Surface, 0),
    cull: materialNumber(dto.Cull, 2),
    useVertexColor: materialNumber(dto.UseVertexColor, 0),
    fresnelPower: materialNumber(dto.FresnelPower, 0),
    shaderName: dto.ShaderName ?? "",
    smoothness: materialNumber(dto.Smoothness, 0),
    metallic: materialNumber(dto.Metallic, 0),
    bumpScale: materialNumber(dto.BumpScale, 0),
    smoothnessLow: materialNumber(dto.SmoothnessLow, 0),
    occulusionStrength: materialNumber(dto.OcclusionStrength, 0),
    clearCoat: materialNumber(dto.ClearCoat, 0),
    clearCoatGlossiness: materialNumber(dto.ClearCoatGlossiness, 0),
    clearCoatGlossinessScale: materialNumber(dto.ClearCoatGlossinessScale, 0),
    alphaCutoff: materialNumber(dto.Cutoff, 0.01),
    baseColor: recordToPairs(dto.BaseColor, RGBA),
    specColor: recordToPairs(dto.SpecColor, RGBA),
    emissionColor: recordToPairs(dto.EmissionColor, RGBA),
    fresnelColor: recordToPairs(dto.FresnelColor, RGBA),
    tiling: recordToPairs(dto.Tiling, XY),
    offset: recordToPairs(dto.Offset, XY),
    textures: stringMapToPairs(dto.Textures),
    filterModes: stringMapToPairs(dto.FilterModes),
  };
}

/**
 * Inbound `materialData` payload → web `MaterialDto` (Dynamo shape),
 * merged over the currently cached row so absent fields survive.
 */
export function materialFromWire(
  wire: Record<string, unknown>,
  base: MaterialDto | undefined,
  fallbackId?: string,
): MaterialDto | null {
  const id =
    (typeof wire.id === "string" && wire.id.length > 0 ? wire.id : null)
    ?? base?.ID
    ?? (fallbackId && fallbackId.length > 0 ? fallbackId : null);
  if (!id) return null;

  const merged: Record<string, unknown> = { ...(base ?? {}), ID: id };
  const set = (attr: string, value: unknown): void => {
    if (value !== undefined) merged[attr] = value;
  };

  if (typeof wire.name === "string") set("Name", wire.name);
  if (typeof wire.shaderName === "string") set("ShaderName", wire.shaderName);
  if (typeof wire.userPreset === "boolean") set("UserPreset", wire.userPreset);
  if (typeof wire.workflowMode === "number") set("WorkflowMode", wire.workflowMode);
  if (typeof wire.surface === "number") set("Surface", wire.surface);
  if (typeof wire.cull === "number") set("Cull", wire.cull);
  if (typeof wire.useVertexColor === "number") set("UseVertexColor", wire.useVertexColor);
  if (typeof wire.fresnelPower === "number") set("FresnelPower", wire.fresnelPower);
  if (typeof wire.smoothness === "number") set("Smoothness", wire.smoothness);
  if (typeof wire.metallic === "number") set("Metallic", wire.metallic);
  if (typeof wire.bumpScale === "number") set("BumpScale", wire.bumpScale);
  if (typeof wire.smoothnessLow === "number") set("SmoothnessLow", wire.smoothnessLow);
  if (typeof wire.occulusionStrength === "number") set("OcclusionStrength", wire.occulusionStrength);
  if (typeof wire.clearCoat === "number") set("ClearCoat", wire.clearCoat);
  if (typeof wire.clearCoatGlossiness === "number") set("ClearCoatGlossiness", wire.clearCoatGlossiness);
  if (typeof wire.clearCoatGlossinessScale === "number") {
    set("ClearCoatGlossinessScale", wire.clearCoatGlossinessScale);
  }
  if (typeof wire.alphaCutoff === "number") set("Cutoff", wire.alphaCutoff);
  set("BaseColor", pairsToRecord(wire.baseColor));
  set("SpecColor", pairsToRecord(wire.specColor));
  set("EmissionColor", pairsToRecord(wire.emissionColor));
  set("FresnelColor", pairsToRecord(wire.fresnelColor));
  set("Tiling", pairsToRecord(wire.tiling));
  set("Offset", pairsToRecord(wire.offset));
  set("Textures", pairsToStringMap(wire.textures));
  set("FilterModes", pairsToStringMap(wire.filterModes));

  return merged as MaterialDto;
}
